
import { useState, FormEvent } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    malls: [] as string[],
    revenue: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const mallOptions = [
    '楽天市場',
    'Amazon',
    'Yahoo!ショッピング',
    '自社EC',
    'eBay',
    'その他'
  ];

  const revenueOptions = [
    '300万円未満',
    '300万円〜1,000万円',
    '1,000万円〜3,000万円',
    '3,000万円〜5,000万円',
    '5,000万円以上'
  ];

  const handleMallChange = (mall: string) => {
    setFormData(prev => ({
      ...prev,
      malls: prev.malls.includes(mall)
        ? prev.malls.filter(m => m !== mall)
        : [...prev.malls, mall]
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('company', formData.company);
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('malls', formData.malls.join(', '));
      formBody.append('revenue', formData.revenue);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d60077hdkj8fk0538hm0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          company: '',
          name: '',
          email: '',
          malls: [],
          revenue: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-24 bg-primary">
      <div className="max-w-[900px] mx-auto px-6 md:px-8">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-3 md:mb-4">
            まずは<strong>"詰まりポイント"</strong>を<br />特定しませんか？
          </h2>
          <p className="text-sm md:text-xl text-white/90">
            無料相談で、現状の課題を整理します
          </p>
        </div>
        
        <div className="bg-white rounded-2xl p-5 md:p-8 lg:p-12 shadow-2xl">
          <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
            <div>
              <label htmlFor="company" className="block text-xs md:text-sm font-bold text-gray-900 mb-1.5 md:mb-2">
                会社名 / 屋号 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="company"
                name="company"
                required
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm md:text-base"
                placeholder="株式会社〇〇"
              />
            </div>
            
            <div>
              <label htmlFor="name" className="block text-xs md:text-sm font-bold text-gray-900 mb-1.5 md:mb-2">
                担当者名 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm md:text-base"
                placeholder="山田 太郎"
              />
            </div>
            
            <div>
              <label htmlFor="email" className="block text-xs md:text-sm font-bold text-gray-900 mb-1.5 md:mb-2">
                メールアドレス <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm md:text-base"
                placeholder="example@company.com"
              />
            </div>
            
            <div>
              <label className="block text-xs md:text-sm font-bold text-gray-900 mb-2 md:mb-3">
                運用モール（複数選択可） <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
                {mallOptions.map((mall) => (
                  <label
                    key={mall}
                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1.5 md:p-2 rounded-lg transition-colors"
                  >
                    <input
                      type="checkbox"
                      name="malls"
                      value={mall}
                      checked={formData.malls.includes(mall)}
                      onChange={() => handleMallChange(mall)}
                      className="w-4 h-4 md:w-5 md:h-5 text-primary focus:ring-primary cursor-pointer"
                    />
                    <span className="text-xs md:text-sm text-gray-700">{mall}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div>
              <label htmlFor="revenue" className="block text-xs md:text-sm font-bold text-gray-900 mb-1.5 md:mb-2">
                月商レンジ <span className="text-red-500">*</span>
              </label>
              <select
                id="revenue"
                name="revenue"
                required
                value={formData.revenue}
                onChange={(e) => setFormData({ ...formData, revenue: e.target.value })}
                className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer text-sm md:text-base"
              >
                <option value="">選択してください</option>
                {revenueOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="message" className="block text-xs md:text-sm font-bold text-gray-900 mb-1.5 md:mb-2">
                困っていること <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={4}
                className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none text-sm md:text-base"
                placeholder="現在困っていることや、自動化したい業務について具体的にお聞かせください（500文字以内）"
              ></textarea>
              <div className="text-right text-xs md:text-sm text-gray-500 mt-1">
                {formData.message.length} / 500
              </div>
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting || formData.malls.length === 0}
              className="w-full bg-primary text-white py-3 md:py-4 rounded-lg font-bold text-sm md:text-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? '送信中...' : '無料相談を申し込む'}
            </button>
            
            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-3 md:px-4 py-2.5 md:py-3 rounded-lg text-center text-xs md:text-base">
                送信が完了しました。担当者より3営業日以内にご連絡いたします。
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-3 md:px-4 py-2.5 md:py-3 rounded-lg text-center text-xs md:text-base">
                送信に失敗しました。お手数ですが、もう一度お試しください。
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
